import axios, { AxiosError, AxiosRequestConfig } from 'axios';

import { API_BASE_URL } from './constant';
import {
  clearSession,
  getAccessToken,
  storeSession,
  getSessionGeneration,
  getSessionIdentity,
  identityOfAccess,
  isSessionReplaced,
  markSessionRefreshed,
  SESSION_MARKER_KEYS,
  storeAccess,
} from './session';
import { LOGIN_PATH } from 'router/pathName';
import { languageHeaders, requestLanguage } from './requestLanguage';

/**
 * APIクライアント。
 *
 * ADM-02。以前の実装には3つの問題があった。
 *  1. `error.response.status` を無条件で読むため、通信断・timeout・CORS拒否で
 *     TypeErrorになり、本来の失敗理由が失われていた。
 *  2. refreshに失敗しても `Promise.reject` せず `undefined` を返しており、
 *     呼び出し側は「成功したが応答が空」として扱っていた。
 *  3. 同時に401になった複数の要求がそれぞれrefreshを送り、後勝ちで
 *     token対を上書きしていた。回転設定の下では先行分が無効になる。
 *
 * ここでは、refreshを単一のPromiseに集約し、失敗は必ずrejectで返す。
 *
 * ADM-17（2026-10-04 PM 決定）。refresh は HttpOnly Cookie（JavaScript から読めない）。
 * ログイン・更新・ログアウトは管理画面専用の経路（/v1/web/）へ、資格情報（Cookie）と
 * CSRF 用のヘッダを付けて送る。access はメモリだけ（session.ts）。
 */

type RetriableConfig = AxiosRequestConfig & {
  _retried?: boolean;
  /**
   * 要求を最初に送り出した時点のセッション（識別子と更新の世代）。401 の後の更新・
   * 再送は、「今の」セッションではなくこの値を基準に判断する。同じタブで
   * ログインし直した後に、前の利用者の要求を新しいトークンで送らないため。
   */
  _sentWith?: HeldSession;
};

/** ADM-17。管理画面のセッションの経路（サーバー: api/web_session.py）。 */
export const LOGIN_ENDPOINT = '/v1/web/token';
const REFRESH_PATH = '/v1/web/session/refresh';
export const LOGOUT_PATH = '/v1/web/session/logout';

/**
 * ADM-17。Cookie を使う要求に付ける CSRF 用のヘッダ。サーバーはこのヘッダと
 * 許可した Origin の両方が無ければ拒否する。別の Origin の画面がこのヘッダを
 * 付けるには CORS の事前確認が要り、許可していない Origin は通らない。
 */
const CSRF_HEADERS = { 'X-Requested-By': 'yometel-admin' };

/** 再送しても副作用が重ならない（読むだけの）メソッド。 */
const SAFE_METHODS = new Set(['get', 'head', 'options']);

/**
 * ADM-02-AC2。サーバーが要求を処理する前（認証段階）に返す401のcode。
 *
 * DRF + simplejwt 5.3.1 の JWTAuthentication と権限確認は view の処理より前に
 * 動き、拒否した場合は次のcodeで401を返す（api/exceptions.py が code を付ける）。
 * このcodeの401なら更新系の要求も処理されていないので、refresh後に再送してよい。
 */
const PRE_PROCESSING_401_CODES = new Set([
  'token_not_valid',
  'not_authenticated',
  'bad_authorization_header',
  'user_not_found',
  'user_inactive',
  'password_changed',
]);

/**
 * 401の後に元の要求を再送してよいか。
 *
 * 取得系は何度送っても結果が変わらないので常に再送する。更新系（POST/PUT/
 * PATCH/DELETE）は、サーバーが処理していないと確かな401のときだけ再送する。
 * それ以外の401は処理済みの可能性があり、再送すると二重に実行されうる。
 */
function canReplayAfter401(config: AxiosRequestConfig, data: unknown): boolean {
  const method = (config.method ?? 'get').toLowerCase();
  if (SAFE_METHODS.has(method)) return true;
  const code =
    data && typeof data === 'object' ? (data as { code?: unknown }).code : undefined;
  return typeof code === 'string' && PRE_PROCESSING_401_CODES.has(code);
}

/** 進行中の更新。どのセッションのための更新かも持つ（別のセッションは待ち合わせない）。 */
let refreshInFlight: { session: string | null; promise: Promise<void> } | null = null;

/** 更新の時間設定（テストで短くできるよう、まとめておく）。 */
export const refreshTiming = {
  /** 更新の応答を待つ上限。超えたらロックを手放し、再試行できるエラーにする。 */
  timeoutMs: 15000,
};

/**
 * 更新の応答が返らなかった（timeout・通信断）。認証の失敗ではないので、
 * セッションを終えずに呼び出し側へ返す。後で操作し直せば再試行される。
 */
export class RefreshUnavailableError extends Error {
  readonly code?: string;
  readonly cause?: unknown;
  constructor(cause: unknown) {
    super((cause as Error)?.message ?? 'Token refresh did not respond');
    this.name = 'RefreshUnavailableError';
    this.code = (cause as { code?: string })?.code;
    this.cause = cause;
  }
}

function isNoResponse(error: unknown): boolean {
  const e = error as { isAxiosError?: boolean; response?: unknown };
  return Boolean(e?.isAxiosError) && !e.response;
}

/** 待っている間に、別のタブでログインし直された（別のセッション・別の利用者）。 */
export class SessionChangedError extends Error {
  constructor() {
    super('The session was replaced in another tab');
    this.name = 'SessionChangedError';
  }
}

/**
 * この接続では Cookie を書き換える処理をタブ間で直列にできない（Web Locks が無い・
 * secure context でない）。直列にできないまま送らない（PR #90 レビュー P2）。
 * 認証の失敗ではないので、セッションは終えない。ログイン画面で理由を表示する
 * （config/sessionEnvironment.ts）。
 */
export class SessionEnvironmentError extends Error {
  constructor() {
    super('Web Locks are not available; the admin console needs https or localhost');
    this.name = 'SessionEnvironmentError';
  }
}

/** 認証の失敗ではなく、セッションを終えずに呼び出し側へ返す失敗。 */
function isTransientSessionError(error: unknown): boolean {
  return error instanceof RefreshUnavailableError || error instanceof SessionEnvironmentError;
}

/** 要求を送った時点で、このタブが持っていたセッション。 */
type HeldSession = { session: string | null; generation: string | null };

/** タブ間で更新・ログイン・ログアウトを1つずつにするための Web Locks の名前。 */
const REFRESH_LOCK = 'yometel-admin-token-refresh';

type LockManagerLike = {
  request: (name: string, callback: () => Promise<unknown>) => Promise<unknown>;
};

function getLockManager(): LockManagerLike | null {
  const locks = typeof navigator !== 'undefined' ? (navigator as any).locks : undefined;
  return locks && typeof locks.request === 'function' ? locks : null;
}

/**
 * Cookie を書き換えうる処理（更新・ログイン・ログアウト）を、タブ間で1つずつにする。
 *
 * 更新の応答（回転した Cookie）とログイン・ログアウトの応答が入れ違うと、ログアウト
 * 後に使える Cookie が残ったり、新しいログインの Cookie が前のセッションのもので
 * 上書き・削除されたりする。Web Locks が無ければ実行しない（SessionEnvironmentError）。
 * 管理画面は https か localhost でだけ使う方針（PR #90 レビュー P2、2026-10-04）。
 */
function withSessionLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = getLockManager();
  if (!locks) return Promise.reject(new SessionEnvironmentError());
  return locks.request(REFRESH_LOCK, fn) as Promise<T>;
}

/** 管理画面のセッションの経路へ、資格情報（Cookie）と CSRF 用のヘッダを付けて送る。 */
function postWebSession(path: string, body: Record<string, string> | null = null) {
  return axios.post(`${API_BASE_URL}${path}`, body, {
    withCredentials: true,
    // EV-271。表示言語も送る（エラー文の言語をそろえる）。
    headers: { ...CSRF_HEADERS, ...languageHeaders() },
    timeout: refreshTiming.timeoutMs,
  });
}

/** セッションの識別子（「sid:user_id」）の sid。 */
function sidOf(identity: string | null): string | null {
  if (!identity) return null;
  const sid = identity.slice(0, identity.indexOf(':'));
  return sid || null;
}

/**
 * Cookie の refresh を失効させる（錠の中で呼ぶ）。sid を添えると、サーバーは Cookie が
 * そのログインのものでなければ失効させず、Cookie も消さない（409 session_mismatch）。
 * 後始末のログアウトが、別のタブの新しいログインを壊さないため。
 */
async function revokeCookie(sid: string | null): Promise<boolean> {
  try {
    await postWebSession(LOGOUT_PATH, sid ? { sid } : null);
    return true;
  } catch {
    return false;
  }
}

/**
 * Cookie の refresh で access を取り直す（錠の中で呼ぶ）。session はこのタブのセッション。
 * 返った access が、このタブのセッション（同じログイン・同じ利用者）のもので
 * なければ使わない（SessionChangedError）。
 */
async function refreshWithinLock(session: string): Promise<void> {
  if (getSessionIdentity() !== session) throw new SessionChangedError();
  let res;
  try {
    res = await postWebSession(REFRESH_PATH);
  } catch (error) {
    if (getSessionIdentity() !== session) throw new SessionChangedError();
    // 応答が無い（timeout・通信断）は認証の失敗ではない。セッションは終えない。
    if (isNoResponse(error)) throw new RefreshUnavailableError(error);
    throw error;
  }
  const access = res?.data?.access;
  if (!access) throw new Error('Refresh response did not contain an access token');
  const current = getSessionIdentity();
  if (current === null) {
    // 応答を待つ間に別のタブでログアウトされた。回転した Cookie を受け取ってしまった
    // ので、ログアウト後に使える Cookie を残さないよう失効させる。錠を放す前に終える
    // （応答の Set-Cookie が、後から始まる新しいログインの Cookie を消さないように。
    // PR #90 レビュー 4176276497）。失敗は無視する。
    await revokeCookie(sidOf(session));
    throw new SessionChangedError();
  }
  // 応答を待つ間に別のタブでログインし直された、または Cookie が別のログインのもの。
  // 別のセッションのトークンを、このタブのセッションとして使わない。
  if (current !== session || identityOfAccess(access) !== session) {
    throw new SessionChangedError();
  }
  storeAccess(access);
  // 更新の世代を進める（endSession が、送った後に別のタブが更新したかを見分ける）。
  markSessionRefreshed();
}

/**
 * Cookie の refresh で access を取り直す。進行中の更新があれば待ち合わせる。
 *
 * refresh は使うたびに回転する（#87）。Cookie はタブ間で共有なので、複数のタブが
 * 同時に更新すると、サーバーは遅い側（回転前の Cookie）を拒否する。
 *  - Web Locks で、タブ間で更新を1つずつにする（後のタブは回転後の Cookie で送る）。
 *    Web Locks が無い接続では送らない（SessionEnvironmentError）。以前あった
 *    「拒否された後に別のタブの更新を待って取り直す」代替経路は、Web Locks の無い
 *    接続を対象外にしたので削除した。
 *  - 返った access が、このタブのセッション（同じログイン・同じ利用者）のもので
 *    なければ使わない（SessionChangedError）。Cookie は別のタブのログインで
 *    置き換わっていることがある。
 */
function refreshAccessToken(expectedSession?: string | null): Promise<void> {
  const session = getSessionIdentity();
  if (expectedSession !== undefined && expectedSession !== session) {
    // 要求を送ったときのセッションではなくなっている。今のセッションを更新しない。
    return Promise.reject(new SessionChangedError());
  }
  if (refreshInFlight && refreshInFlight.session === session) return refreshInFlight.promise;
  if (!session) {
    return Promise.reject(new Error('No session to refresh'));
  }

  const promise = withSessionLock(() => refreshWithinLock(session)).finally(() => {
    if (refreshInFlight?.promise === promise) refreshInFlight = null;
  });
  refreshInFlight = { session, promise };

  return promise;
}

/**
 * セッションを終わらせ、ログイン画面へ戻す。
 *
 * held を渡したときは、要求を送った時点のセッションのままで、その後に別のタブが
 * 更新していないときだけ印を消す。
 *  - 別のタブでログインし直していた: 消さず、このタブだけログイン画面へ戻す
 *    （このタブでログインし直した場合は、このタブはそのまま）
 *  - 同じセッションを別のタブが更新していた（Cookie は新しい）: 消さない
 */
function endSession(held?: HeldSession): void {
  if (held) {
    const current = getSessionIdentity();
    if (current !== null && current !== held.session) {
      if (isSessionReplaced()) goToLogin();
      return;
    }
    if (current !== null && getSessionGeneration() !== held.generation) return;
  }
  clearSession();
  goToLogin();
}

function goToLogin(): void {
  if (typeof window !== 'undefined' && window.location.pathname !== LOGIN_PATH) {
    window.location.replace(LOGIN_PATH);
  }
}

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
});

/** ログインの結果。拒否（401・403 など）は例外で返す。 */
export type LoginOutcome = 'ok' | 'not_manager' | 'cookie_unavailable' | 'network';

/**
 * ADM-17。管理画面のログイン。refresh はサーバーが HttpOnly Cookie で返し、access は
 * メモリに置く。
 *
 * ログイン・役割の確認（RV-040）・Cookie で更新できることの確認・失敗したときの後始末を、
 * まとめて1つの錠の中で行う（PR #90 レビュー 4176276491）。錠を /v1/web/token の応答で
 * 放すと、確認と後始末が錠の外で走り、2つのタブで同時にログインしたとき、先のタブの
 * 後始末が後のタブの成功したログインを消し得た。後始末は自分が発行したセッションだけを
 * 対象にする（印は自分のものなら消す、Cookie は sid を添えて失効させる）。
 *
 * keepSignedIn はログインの画面の「ログイン状態を保持する」（既定は外れ）。
 */
export function loginSession(
  email: string,
  password: string,
  keepSignedIn = false,
  config: AxiosRequestConfig = {}
): Promise<LoginOutcome> {
  return withSessionLock(async () => {
    // BE-05（2026-10-14 PM 決定）。「ログイン状態を保持する」を送る。期限はサーバーが決める
    // （付ければ使うたびに1日延ばして最初のログインから30日まで、外せばログインから1日）。
    const response = await axiosInstance.post(
      LOGIN_ENDPOINT,
      { email, password, keep_signed_in: keepSignedIn },
      { ...config, withCredentials: true, headers: { ...CSRF_HEADERS } }
    );
    const access = response?.data?.access;
    if (!access) throw new Error('Login response did not contain an access token');
    storeSession(access);
    const mine = identityOfAccess(access);

    const discard = async () => {
      if (getSessionIdentity() === mine) clearSession();
      await revokeCookie(sidOf(mine));
    };

    // RV-040。役割をサーバーのプロフィールで確かめる。錠の中なので、401 でも更新しない
    // （更新は同じ錠を待つため）。
    let role: unknown;
    try {
      const profile = await axiosInstance.get('/v1/profile/', {
        _retried: true,
      } as RetriableConfig);
      role = profile?.data?.role;
    } catch (error) {
      await discard();
      throw error;
    }
    if (role !== 'manager') {
      await discard();
      return 'not_manager';
    }
    storeSession(access, 'manager');

    // ADM-17。移動先は再読み込みになり、access はメモリから消える。Cookie で取り直せる
    // ことをここで一度確かめる。管理画面と API が別サイト（例: 127.0.0.1 と localhost）
    // だと Cookie が保存されず、移動先からログイン画面へ戻り続けてしまうため。
    try {
      await refreshWithinLock(mine as string);
    } catch (error) {
      await discard();
      return error instanceof RefreshUnavailableError ? 'network' : 'cookie_unavailable';
    }
    return 'ok';
  });
}

/**
 * ADM-17 / ADM-23。Cookie の refresh を失効させる（サーバーが Cookie も消す）。
 * access は要らない。このタブのセッションの sid を添え、Cookie が別のログインのものなら
 * 失効させない。失効を確認できたら true、できなければ（400・409・通信断）false。
 */
export function logoutSession(): Promise<boolean> {
  const sid = sidOf(getSessionIdentity());
  // Web Locks が無い接続では送らない（失効は確認できない）。
  return withSessionLock(() => revokeCookie(sid)).catch(() => false);
}

/**
 * 別のタブでログアウト・ログインし直されたら、このタブをログイン画面へ戻す。
 * 新しいセッションの印は消さない（別のタブで使われている）。
 * 同じセッションの更新（世代の変化）では何もしない。
 */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event: StorageEvent) => {
    if (event.key !== null && !SESSION_MARKER_KEYS.includes(event.key)) return;
    if (isSessionReplaced({ countLogout: true })) goToLogin();
  });
}

axiosInstance.interceptors.request.use(
  async (config) => {
    if (config.url === LOGIN_ENDPOINT) return withLanguage(config);
    // 別のタブでセッションが入れ替わっていたら、このタブで準備した要求を
    // 新しいセッションで送らない。
    if (isSessionReplaced()) {
      goToLogin();
      return Promise.reject(new SessionChangedError());
    }
    const retriable = config as RetriableConfig;
    if (retriable._sentWith === undefined) {
      // 最初の送出。このときのセッションを要求に記録する。
      retriable._sentWith = { session: getSessionIdentity(), generation: getSessionGeneration() };
    } else if (retriable._sentWith.session !== getSessionIdentity()) {
      // 再送。最初に送ったときのセッションではなくなっていたら送らない。
      return Promise.reject(new SessionChangedError());
    }
    // ADM-17。再読み込み直後・新しいタブでは access がメモリに無い。セッションの印が
    // あれば、送る前に Cookie の refresh で取り直す。
    if (!getAccessToken() && retriable._sentWith.session !== null) {
      try {
        await refreshAccessToken(retriable._sentWith.session);
      } catch (refreshError) {
        if (!isTransientSessionError(refreshError)) endSession(retriable._sentWith);
        return Promise.reject(refreshError);
      }
    }
    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    } else {
      // tokenが無いのに `Bearer null` を送ると、サーバー側のログが読めなくなる。
      delete config.headers.Authorization;
    }
    return withLanguage(config);
  },
  (error) => Promise.reject(error)
);

/** サーバーは要求の言語でエラー文を返す。表示言語（i18n と同じ保存値）に合わせる。 */
function withLanguage<T extends AxiosRequestConfig>(config: T): T {
  if (config.headers) {
    (config.headers as any)['Accept-Language'] = requestLanguage();
  }
  return config;
}

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = (error.config ?? {}) as RetriableConfig;
    const status = error.response?.status;

    // 応答が無い＝通信断・timeout・CORS拒否。認証の問題と混同しない。
    if (status === undefined) {
      return Promise.reject(error);
    }

    // ログイン自体の失敗は、そのまま呼び出し側へ返す。
    if (originalRequest.url === LOGIN_ENDPOINT) {
      return Promise.reject(error);
    }

    if (status !== 401 || originalRequest._retried) {
      return Promise.reject(error);
    }
    if (!canReplayAfter401(originalRequest, error.response?.data)) {
      return Promise.reject(error);
    }

    originalRequest._retried = true;
    // 判断の基準は、要求を送り出した時点のセッション（今のものではない）。
    const held: HeldSession = originalRequest._sentWith ?? {
      session: getSessionIdentity(),
      generation: getSessionGeneration(),
    };
    if (held.session === null) {
      // セッションが無い（ログイン前）。更新もやり直しもしない。
      return Promise.reject(error);
    }
    if (getSessionIdentity() !== held.session) {
      // 応答を待つ間にセッションが替わった（このタブ・別のタブでのログインし直し、
      // ログアウト）。前のセッションの要求を、今のセッションで更新・再送しない。
      if (isSessionReplaced({ countLogout: true })) goToLogin();
      return Promise.reject(new SessionChangedError());
    }
    try {
      await refreshAccessToken(held.session);
    } catch (refreshError) {
      if (isTransientSessionError(refreshError)) {
        return Promise.reject(refreshError);
      }
      endSession(held);
      return Promise.reject(refreshError);
    }
    if (getSessionIdentity() !== held.session) {
      // 更新を待つ間にログインし直された。このタブで準備した要求を別のセッション
      // （別の利用者かもしれない）のトークンで送らない。
      endSession(held);
      return Promise.reject(new SessionChangedError());
    }
    return axiosInstance(originalRequest);
  }
);

export { endSession, refreshAccessToken };
export default axiosInstance;
